import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ArticleCard from '@/components/feature/ArticleCard';
import ScrollToTop from '@/components/feature/ScrollToTop';
import SEOHead from '@/components/feature/SEOHead';
import SchemaOrg from '@/components/feature/SchemaOrg';
import { featuredArticles, hotArticles, getLatestArticles, categories, articles } from '@/mocks/articles';

const heroArticle = articles.find(a => a.id === '3')!;
const subHeroArticles = articles.filter(a => a.isFeatured && a.id !== '3').slice(0, 2);

export default function Home() {
  return (
    <>
      <ScrollToTop />
      <SEOHead />
      <SchemaOrg type="Organization" />
      <SchemaOrg type="WebSite" />
      <SchemaOrg
        type="WebPage"
        data={{
          name: '亚洲综合网 - 亚洲资讯娱乐一站式平台',
          url: 'https://985huidian.cn',
          description: '亚洲综合网是专注亚洲地区的综合性信息平台，汇聚亚洲娱乐、财经、科技、生活等全品类内容。',
        }}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main>
          {/* Hero Section */}
          <section className="max-w-[1280px] mx-auto px-4 md:px-6 pt-6 pb-10">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
              <Link
                to={`/article/${heroArticle.id}`}
                className="lg:col-span-2 group block relative overflow-hidden rounded-xl bg-background-100 h-[420px] md:h-[500px] cursor-pointer"
              >
                <img
                  src={heroArticle.image}
                  alt={heroArticle.title}
                  className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-black/10"></div>
                <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8">
                  <span className="inline-block px-3 py-1 rounded-md text-xs font-semibold bg-primary-500 text-background-50 mb-4">
                    {heroArticle.categoryName}
                  </span>
                  <h1 className="font-heading font-bold text-2xl md:text-4xl text-background-50 group-hover:text-primary-200 transition-colors mb-3 leading-tight">
                    {heroArticle.title}
                  </h1>
                  <p className="text-sm md:text-base text-background-200 line-clamp-2 mb-4 max-w-2xl">
                    {heroArticle.excerpt}
                  </p>
                  <div className="flex items-center gap-4 text-sm text-background-300">
                    <span>{heroArticle.author}</span>
                    <span>{heroArticle.date}</span>
                    <span>{heroArticle.views.toLocaleString()} 阅读</span>
                  </div>
                </div>
              </Link>

              <div className="flex flex-col gap-5">
                {subHeroArticles.map((article) => (
                  <Link
                    key={article.id}
                    to={`/article/${article.id}`}
                    className="group block relative overflow-hidden rounded-xl bg-background-100 h-[242px] cursor-pointer"
                  >
                    <img
                      src={article.image}
                      alt={article.title}
                      className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-black/10"></div>
                    <div className="absolute bottom-0 left-0 right-0 p-5">
                      <span className="inline-block px-2.5 py-1 rounded-md text-xs font-semibold bg-accent-500 text-background-50 mb-3">
                        {article.categoryName}
                      </span>
                      <h2 className="font-heading font-bold text-base md:text-lg text-background-50 group-hover:text-accent-200 transition-colors line-clamp-2 leading-snug">
                        {article.title}
                      </h2>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>

          {/* Latest News Ticker */}
          <section className="border-y border-background-200/70 bg-background-100/50">
            <div className="max-w-[1280px] mx-auto px-4 md:px-6 py-3 flex items-center gap-4">
              <span className="flex-shrink-0 px-3 py-1 rounded-full text-xs font-semibold bg-primary-500 text-background-50 whitespace-nowrap">
                最新快讯
              </span>
              <div className="overflow-hidden flex-1">
                <div className="flex items-center gap-8 text-sm text-foreground-700 whitespace-nowrap">
                  {getLatestArticles(8).map((a, i) => (
                    <Link
                      key={a.id}
                      to={`/article/${a.id}`}
                      className="hover:text-primary-600 transition-colors"
                    >
                      <span className="text-primary-500 font-medium mr-1">{a.date}</span>
                      {a.title.slice(0, 30)}...
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          </section>

          {/* Category Sections */}
          {categories.map((cat) => {
            const catArticles = articles.filter(a => a.category === cat.slug).slice(0, 4);
            if (catArticles.length === 0) return null;
            return (
              <section key={cat.slug} className="max-w-[1280px] mx-auto px-4 md:px-6 py-10">
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-3">
                    <div className="w-1 h-6 rounded-full bg-primary-500"></div>
                    <h2 className="font-heading font-bold text-xl md:text-2xl text-foreground-950">
                      {cat.name}
                    </h2>
                  </div>
                  <Link
                    to={`/category/${cat.slug}`}
                    className="flex items-center gap-1 text-sm text-foreground-600 hover:text-primary-600 transition-colors whitespace-nowrap font-medium"
                  >
                    查看全部
                    <i className="ri-arrow-right-line"></i>
                  </Link>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                  {catArticles.map((article) => (
                    <ArticleCard key={article.id} article={article} />
                  ))}
                </div>
              </section>
            );
          })}

          {/* Hot Articles */}
          <section className="bg-background-100/60 border-y border-background-200/70">
            <div className="max-w-[1280px] mx-auto px-4 md:px-6 py-10">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-1 h-6 rounded-full bg-accent-500"></div>
                <h2 className="font-heading font-bold text-xl md:text-2xl text-foreground-950">
                  热门文章
                </h2>
                <i className="ri-fire-line text-accent-500 text-xl"></i>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                {hotArticles.slice(0, 6).map((article, idx) => (
                  <div key={article.id} className="flex items-start gap-4 p-4 rounded-xl bg-background-50 hover:bg-background-100 transition-colors">
                    <span className="flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-lg bg-accent-100 text-accent-700 font-bold text-sm">
                      {idx + 1}
                    </span>
                    <div className="flex-1 min-w-0">
                      <Link
                        to={`/article/${article.id}`}
                        className="font-medium text-sm text-foreground-950 hover:text-primary-600 transition-colors line-clamp-2 cursor-pointer"
                      >
                        {article.title}
                      </Link>
                      <div className="flex items-center gap-3 mt-2 text-xs text-foreground-500">
                        <span>{article.categoryName}</span>
                        <span>{article.date}</span>
                        <span>{article.views.toLocaleString()} 阅读</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Latest Articles Grid */}
          <section className="max-w-[1280px] mx-auto px-4 md:px-6 py-10">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-1 h-6 rounded-full bg-secondary-500"></div>
              <h2 className="font-heading font-bold text-xl md:text-2xl text-foreground-950">
                最新文章
              </h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {getLatestArticles(9).map((article) => (
                <ArticleCard key={article.id} article={article} variant="default" />
              ))}
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}